import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { UserInvitationModel } from 'src/Libs/installation-rights-management/models'
import { ApiInviteType } from 'src/Libs/api/types'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import useInstallationInvitation from 'src/Libs/installation-rights-management/hooks/use-installation-invitation'
import { InstallationsContext } from 'src/Libs/installations/context/installations-context'

const ACCEPT_TIMEOUT_UNTIL_RELOAD = 15000

interface UseInvitationsDialogProps {
  userInvitations?: UserInvitationModel[]
  isUserInvitationLoading?: boolean
  invitationToken?: string
  onClose?: () => void
}

interface UseInvitationsDialogReturn {
  invitations: UserInvitationModel[]
  isDataLoading: boolean
  isAcceptLoading: boolean
  isDeclineLoading: boolean
  open: boolean
  activeToken: string | null
  onAccept: (tokens?: string[]) => Promise<void>
  onDecline: (token?: string) => Promise<void>
  handleClose: () => void
  isAwaitingRefetch: boolean
}

const useInvitationsDialog = ({
  userInvitations,
  invitationToken,
  onClose,
  isUserInvitationLoading,
}: UseInvitationsDialogProps): UseInvitationsDialogReturn => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const installationContext = useContext(InstallationsContext)
  const { enqueueSuccessStack } = useSnackbar()
  const { t } = useCustomTranslation()

  const [activeToken, setActiveToken] = useState<string | null>(null)
  const [handledTokens, setHandledTokens] = useState<string[]>([])
  const [open, setOpen] = useState(true)
  const [isAwaitingRefetch, setIsAwaitingRefetch] = useState(false)

  const { getInvitationByToken, acceptInvitation, declineInvitation, isAcceptLoading, isDeclineLoading } = useInstallationInvitation()
  const { data: tokenInvitationData, isLoading: tokenLoading } = getInvitationByToken(invitationToken)

  const invitations = useMemo(() => {
    let all: UserInvitationModel[]

    if (invitationToken && tokenInvitationData) {
      all = [
        {
          invitationToken,

          installationId: tokenInvitationData.installationId,

          inviteType: tokenInvitationData.inviteType as ApiInviteType,

          projectName: tokenInvitationData.projectName,

          partnersInstallationName: tokenInvitationData.partnersInstallationName,
        } as UserInvitationModel,
      ]
    } else if (userInvitations && !isUserInvitationLoading) {
      all = Array.isArray(userInvitations) ? userInvitations : [userInvitations]
    } else {
      all = []
    }

    return all.filter(inv => !handledTokens.includes(inv.invitationToken))
  }, [userInvitations, invitationToken, tokenInvitationData, isUserInvitationLoading, handledTokens])

  const handleClose = (): void => {
    setOpen(false)
    onClose?.()
  }

  const closeIfAllHandled = (newHandled: string[]): void => {
    if (invitations.every(inv => newHandled.includes(inv.invitationToken))) handleClose()
  }

  const reloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current)
    }
  }, [])

  const onAccept = async (tokens: string[] = invitations.map(i => i.invitationToken).filter(Boolean)): Promise<void> => {
    setActiveToken(tokens[0])
    const accepted: string[] = []

    for (const token of tokens) {
      try {
        await acceptInvitation(token)
        accepted.push(token)
      } catch {
        setActiveToken(null)
        if (accepted.length > 0) {
          const newHandled = [...handledTokens, ...accepted]
          setHandledTokens(newHandled)
        }
        dispatch(refreshApiView())
        if (invitationToken || invitations.length === 1) handleClose()
        return
      }
    }

    dispatch(refreshApiView())

    const newHandled = [...handledTokens, ...accepted]
    setHandledTokens(newHandled)

    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current)
    setIsAwaitingRefetch(true)
    reloadTimerRef.current = setTimeout(async () => {
      await installationContext?.refreshInstallations()

      enqueueSuccessStack(t('installation.invitation.success'))

      setActiveToken(null)
      closeIfAllHandled(newHandled)

      setIsAwaitingRefetch(false)
      navigate('/')
    }, ACCEPT_TIMEOUT_UNTIL_RELOAD)
  }

  const onDecline = async (token: string = invitations[0]?.invitationToken ?? ''): Promise<void> => {
    if (!token) return
    setActiveToken(token)
    try {
      await declineInvitation(token)
      dispatch(refreshApiView())
      const newHandled = [...handledTokens, token]
      setHandledTokens(newHandled)
      closeIfAllHandled(newHandled)
    } catch {
      dispatch(refreshApiView())
      if (invitationToken || invitations.length === 1) handleClose()
    } finally {
      setActiveToken(null)
    }
  }

  const isDataLoading = tokenLoading || !!(isUserInvitationLoading && !invitationToken)

  return {
    invitations,
    isDataLoading,
    isAcceptLoading,
    isDeclineLoading,
    open,
    activeToken,
    isAwaitingRefetch,
    onAccept,
    onDecline,
    handleClose,
  }
}

export default useInvitationsDialog
